import {flushSync} from 'react-dom';
import {createRoot} from 'react-dom/client';

const roots = new WeakMap();
const pendingUnmounts = new WeakMap();

const scheduleAfterCommit = (callback) => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(callback);
    return;
  }
  setTimeout(callback, 0);
};

export const renderReactNode = (node, container, {sync = true} = {}) => {
  if (!container) {
    return null;
  }

  let root = roots.get(container);
  if (!root) {
    root = createRoot(container);
    roots.set(container, root);
  }
  pendingUnmounts.delete(container);

  // Commit synchronously for imperative callers (Mapbox popups via
  // setDOMContent) that read the container's DOM in the same tick they render
  // into it; createRoot().render() commits asynchronously, so without flushSync
  // the node would still be empty when read. This preserves the synchronous
  // contract of the legacy ReactDOM.render these call sites relied on.
  //
  // Pass {sync: false} when rendering can be deferred (e.g. popups). flushSync
  // must not run while React is already rendering — popups are frequently opened
  // from event handlers or effects, which would trigger that invariant.
  if (sync) {
    flushSync(() => {
      root.render(node);
    });
  }
  else {
    root.render(node);
  }
  return root;
};

export const unmountReactNode = (container) => {
  const root = roots.get(container);
  if (!root) {
    return;
  }

  const token = {};
  pendingUnmounts.set(container, token);
  scheduleAfterCommit(() => {
    if (pendingUnmounts.get(container) !== token) {
      return;
    }
    root.unmount();
    roots.delete(container);
    pendingUnmounts.delete(container);
  });
};
